import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'

import {
  useGetAllCoordinates,
  useGetDeliveryAreaGroups,
  useGetDeliverySlots,
  useGetPickupSlots,
  useGetCollectionPoints,
} from 'hooks/useServiceAreas'
import {
  DeliveryAreaGroup,
  SpecificDeliveryAreaGroup,
} from 'types/model/common'
import { DeliverySlot, PickupSlot } from 'types/model/delivery'
import { startOfISOWeek, addDays, formatISO } from 'date-fns'
import { useGetFeeProducts } from 'hooks/useProducts'
import { FeeProduct } from 'types/model/product'
import { CollectionPoint, DeliveryArea } from 'types/model/serviceAreas'
import { usePlace } from 'hooks/usePlaces'

type ServiceAreaProviderProps = {
  placeId: string
  children: ReactNode
}

const initialSelectedCollectionPoint = {
  id: NaN,
  name: '' as string,
  latitude: NaN,
  longitude: NaN,
} as const

const initialSelectedArea = {
  id: NaN,
  name: '' as string,
} as const

export type SelectedAreaProps = typeof initialSelectedArea

type SelectedCollectionPointProps = typeof initialSelectedCollectionPoint

interface ServiceAreasContextTypes {
  isFetchingCoordinates: boolean
  isLoadingCoordinates: boolean
  placeId: string
  isLoadingFeeProducts: boolean
  feeProductsError: unknown
  feeProducts: FeeProduct[]
  deliverySlots: DeliverySlot[]
  weekView: boolean
  setWeekView: (value: boolean) => void
  isLoading: boolean
  error: unknown
  allDeliveryAreas: DeliveryAreaGroup[]
  coordinates: DeliveryArea[][]
  coordinateGroups: SpecificDeliveryAreaGroup[][]
  isLoadingPlace: boolean
  isLoadingSlots: boolean
  isLoadingPickupSlots: boolean
  pickupSlotsError: unknown
  pickupSlots: PickupSlot[]
  slotsError: unknown
  placeError: unknown
  collectionPoints: CollectionPoint[]
  isLoadingCollectionPoints: boolean
  collectionPointsError: unknown
  setSelectedCollectionPoint: (value: SelectedCollectionPointProps) => void
  selectedCollectionPoint: SelectedCollectionPointProps
  storeName: string
  storeLatitude: number
  storeLongitude: number
  setCoordinates: Dispatch<SetStateAction<DeliveryArea[][]>>
  selectedDeliveryArea: SelectedAreaProps
  setSelectedDeliveryArea: (value: SelectedAreaProps) => void
  dateData: { startOfWeek: Date; date: Date }
  setDateData: (value: { startOfWeek: Date; date: Date }) => void
  currencyCode: string
}

const ServiceAreasContext = createContext<ServiceAreasContextTypes | null>(null)

const ServiceAreasProvider = ({
  placeId,
  children,
}: ServiceAreaProviderProps) => {
  const [coordinates, setCoordinates] = useState<DeliveryArea[][]>([[]])
  const [selectedDeliveryArea, setSelectedDeliveryArea] =
    useState<SelectedAreaProps>(initialSelectedArea)
  const [selectedCollectionPoint, setSelectedCollectionPoint] =
    useState<SelectedCollectionPointProps>(initialSelectedCollectionPoint)
  const [dateData, setDateData] = useState({
    startOfWeek: startOfISOWeek(new Date()),
    date: new Date(),
  })
  const [weekView, setWeekView] = useState(true)
  const { isLoading, error, allDeliveryAreas } = useGetDeliveryAreaGroups(
    placeId as string
  )
  const {
    isLoading: isLoadingCollectionPoints,
    error: collectionPointsError,
    collectionPoints,
  } = useGetCollectionPoints(placeId as string)
  const {
    isFetching: isLoadingSlots,
    error: slotsError,
    deliverySlots,
  } = useGetDeliverySlots(
    placeId,
    selectedDeliveryArea.id,
    formatISO(dateData.startOfWeek) as string,
    formatISO(addDays(dateData.startOfWeek, 6)) as string
  )
  const {
    isLoading: isLoadingPickupSlots,
    error: pickupSlotsError,
    pickupSlots,
  } = useGetPickupSlots(
    placeId,
    selectedCollectionPoint.id,
    formatISO(dateData.startOfWeek) as string,
    formatISO(addDays(dateData.startOfWeek, 6)) as string
  )
  const {
    coordinateGroups,
    isLoading: isLoadingCoordinates,
    isFetching: isFetchingCoordinates,
  } = useGetAllCoordinates(allDeliveryAreas, placeId)

  useEffect(() => {
    if (!isLoadingCoordinates && !isFetchingCoordinates) {
      if (selectedDeliveryArea.id) {
        setCoordinates(
          coordinateGroups.filter(
            (coordinateGroup) =>
              coordinateGroup[0].deliveryAreaGroupId === selectedDeliveryArea.id
          )
        )
      } else {
        setCoordinates(coordinateGroups)
      }
    }
  }, [isLoadingCoordinates, isFetchingCoordinates])

  const {
    isLoading: isLoadingFeeProducts,
    error: feeProductsError,
    feeProducts,
  } = useGetFeeProducts(placeId)
  const {
    place,
    isLoading: isLoadingPlace,
    error: placeError,
  } = usePlace(placeId as string)
  const {
    name: storeName,
    latitude: storeLatitude,
    longitude: storeLongitude,
    currencyCode,
  } = place

  return (
    <ServiceAreasContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        isFetchingCoordinates,
        isLoadingCoordinates,
        placeId,
        isLoadingFeeProducts,
        feeProductsError,
        feeProducts,
        isLoadingPickupSlots,
        pickupSlots,
        pickupSlotsError,
        deliverySlots,
        weekView,
        setWeekView,
        selectedDeliveryArea,
        setSelectedDeliveryArea,
        coordinateGroups,
        setCoordinates,
        allDeliveryAreas,
        coordinates,
        isLoading,
        error,
        isLoadingPlace,
        isLoadingSlots,
        slotsError,
        placeError,
        collectionPoints,
        isLoadingCollectionPoints,
        collectionPointsError,
        setSelectedCollectionPoint,
        selectedCollectionPoint,
        storeName,
        storeLatitude,
        storeLongitude,
        dateData,
        setDateData,
        currencyCode,
      }}>
      {children}
    </ServiceAreasContext.Provider>
  )
}

export const useServiceAreas = () =>
  useContext(ServiceAreasContext) as ServiceAreasContextTypes

export default ServiceAreasProvider
